import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  TrendingUp,
  Truck,
  X,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface RateRow {
  jurisdiction_code: string;
  jurisdiction_name: string;
  country: 'US' | 'CA';
  is_ifta_jurisdiction: boolean;
  tax_rate: number | null;
  surcharge_rate: number | null;
}

interface ReturnLine {
  code: string;
  name: string;
  country: 'US' | 'CA';
  is_ifta: boolean;
  miles: number;
  tax_paid_gallons: number;
  taxable_gallons: number;
  net_gallons: number;
  tax_rate: number | null;
  surcharge_rate: number | null;
  tax_due: number;
  surcharge_due: number;
  total_due: number;
}

// Raw rows for the quarter, kept with their truck identifiers so the truck
// filter can be applied client-side without another round trip.
interface MilesRow { truck_id: string | null; jurisdiction_code: string; miles: number | string }
interface PilotFuelRow { vehicle_id: string | null; state: string | null; gallons: number | string }
interface OwnerFuelRow { truck_id: string | null; truck_number: string | null; state: string | null; gallons: number | string }
interface TruckOption { id: string; unit_number: string }

const SURCHARGE_JURISDICTIONS = new Set(['IN', 'KY', 'VA']);

// Unit numbers are matched as text across tables (pilot fuel stores the unit
// in vehicle_id, owner fuel in truck_number), so normalise before comparing.
const normUnit = (u: string | null | undefined) => (u || '').toString().trim().toUpperCase();

function currentQuarter(): string {
  const now = new Date();
  const q = Math.floor(now.getMonth() / 3) + 1;
  return `${now.getFullYear()}-Q${q}`;
}

function shiftQuarter(q: string, delta: number): string {
  const [yStr, qPart] = q.split('-Q');
  let year = parseInt(yStr, 10);
  let quarter = parseInt(qPart, 10) + delta;
  while (quarter > 4) { quarter -= 4; year++; }
  while (quarter < 1) { quarter += 4; year--; }
  return `${year}-Q${quarter}`;
}

function quarterBounds(q: string): { start: string; end: string } {
  const [yStr, qPart] = q.split('-Q');
  const year = parseInt(yStr, 10);
  const quarter = parseInt(qPart, 10);
  const startMonth = (quarter - 1) * 3;
  const start = new Date(Date.UTC(year, startMonth, 1));
  const end = new Date(Date.UTC(year, startMonth + 3, 0));
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

function fmtUSD(n: number): string {
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

function fmtNum(n: number, digits = 2): string {
  return n.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

export function IFTAReturnView({ onGoToMileage }: { onGoToMileage?: () => void } = {}) {
  const toast = useToast();
  const [quarter, setQuarter] = useState<string>(currentQuarter());
  const [loading, setLoading] = useState(false);
  const [rates, setRates] = useState<RateRow[]>([]);
  const [milesRows, setMilesRows] = useState<MilesRow[]>([]);
  const [fuelRows, setFuelRows] = useState<PilotFuelRow[]>([]);
  const [ownerFuelRows, setOwnerFuelRows] = useState<OwnerFuelRow[]>([]);

  // Truck filter. Empty selection = whole fleet (the real, fileable return).
  // Any selection is a live what-if for just those units.
  const [trucks, setTrucks] = useState<TruckOption[]>([]);
  const [selectedTruckIds, setSelectedTruckIds] = useState<Set<string>>(new Set());
  const [truckSearch, setTruckSearch] = useState('');
  const [showTruckPicker, setShowTruckPicker] = useState(false);
  const truckPickerRef = useRef<HTMLDivElement | null>(null);

  const { start, end } = useMemo(() => quarterBounds(quarter), [quarter]);

  useEffect(() => { loadReturn(); }, [quarter]);

  useEffect(() => {
    supabase
      .from('trucks')
      .select('id, unit_number')
      .order('unit_number')
      .then(({ data, error }) => {
        if (error) { console.error('Failed to load trucks for IFTA filter', error); return; }
        setTrucks((data || []) as TruckOption[]);
      });
  }, []);

  // Close the truck picker on outside click.
  useEffect(() => {
    if (!showTruckPicker) return;
    const onDown = (e: MouseEvent) => {
      if (truckPickerRef.current && !truckPickerRef.current.contains(e.target as Node)) {
        setShowTruckPicker(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showTruckPicker]);

  async function loadReturn() {
    setLoading(true);
    try {
      // PostgREST caps each SELECT at 1000 rows; a full quarter of per-truck-
      // per-day-per-state rows easily exceeds that, so paginate via .range().
      async function fetchAll<T>(
        build: (from: number, to: number) => any
      ): Promise<T[]> {
        const pageSize = 1000;
        const out: T[] = [];
        let offset = 0;
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { data, error } = await build(offset, offset + pageSize - 1);
          if (error) throw error;
          const page = (data || []) as T[];
          out.push(...page);
          if (page.length < pageSize) break;
          offset += pageSize;
        }
        return out;
      }

      const [ratesRes, miles, fuel, ownerFuel] = await Promise.all([
        supabase
          .from('ifta_tax_rates')
          .select('jurisdiction_code, jurisdiction_name, country, is_ifta_jurisdiction, tax_rate, surcharge_rate')
          .eq('quarter', quarter),
        fetchAll<MilesRow>((from, to) =>
          supabase
            .from('ifta_state_miles')
            .select('truck_id, jurisdiction_code, miles')
            .gte('trip_date', start)
            .lte('trip_date', end)
            .order('trip_date', { ascending: true })
            .range(from, to)
        ),
        fetchAll<PilotFuelRow>((from, to) =>
          supabase
            .from('pilot_fuel_imports')
            .select('vehicle_id, state, gallons')
            .gte('transaction_date', start)
            .lte('transaction_date', end)
            .order('transaction_date', { ascending: true })
            .range(from, to)
        ),
        // Owner-operator fuel: gallons they paid for out-of-pocket. These are
        // still tax-paid gallons for IFTA purposes (the state collected fuel
        // tax at the pump), so we roll them in with company-card fuel.
        fetchAll<OwnerFuelRow>((from, to) =>
          supabase
            .from('owner_fuel_entries')
            .select('truck_id, truck_number, state, gallons')
            .gte('entry_date', start)
            .lte('entry_date', end)
            .order('entry_date', { ascending: true })
            .range(from, to)
        ),
      ]);

      if (ratesRes.error) throw ratesRes.error;

      setRates((ratesRes.data || []) as RateRow[]);
      setMilesRows(miles);
      setFuelRows(fuel);
      setOwnerFuelRows(ownerFuel);
    } catch (err: any) {
      toast.error(err.message || 'Failed to load IFTA return');
    } finally {
      setLoading(false);
    }
  }

  const isFiltered = selectedTruckIds.size > 0;
  const selectedTrucks = useMemo(
    () => trucks.filter(t => selectedTruckIds.has(t.id)),
    [trucks, selectedTruckIds]
  );
  const selectedUnitSet = useMemo(
    () => new Set(selectedTrucks.map(t => normUnit(t.unit_number))),
    [selectedTrucks]
  );

  // Aggregation. Identical arithmetic to the fleet return; the only difference
  // under a truck filter is which rows feed it (and therefore the MPG, which is
  // the selected units' own MPG rather than the fleet's).
  const { lines, totalMiles, totalGallons, missingRates } = useMemo(() => {
    const rateMap = new Map<string, RateRow>();
    rates.forEach(r => rateMap.set(r.jurisdiction_code, r));

    const keepMiles = (m: MilesRow) => !isFiltered || (!!m.truck_id && selectedTruckIds.has(m.truck_id));
    const keepPilot = (f: PilotFuelRow) => !isFiltered || selectedUnitSet.has(normUnit(f.vehicle_id));
    const keepOwner = (f: OwnerFuelRow) =>
      !isFiltered ||
      (!!f.truck_id && selectedTruckIds.has(f.truck_id)) ||
      selectedUnitSet.has(normUnit(f.truck_number));

    const milesByCode = new Map<string, number>();
    for (const m of milesRows) {
      if (!keepMiles(m)) continue;
      milesByCode.set(m.jurisdiction_code, (milesByCode.get(m.jurisdiction_code) || 0) + Number(m.miles || 0));
    }

    const gallonsByCode = new Map<string, number>();
    for (const f of fuelRows) {
      if (!keepPilot(f)) continue;
      const code = f.state?.toString().toUpperCase();
      if (!code) continue;
      gallonsByCode.set(code, (gallonsByCode.get(code) || 0) + Number(f.gallons || 0));
    }
    // Merge owner-operator fuel (separate table) into the same per-state totals.
    for (const f of ownerFuelRows) {
      if (!keepOwner(f)) continue;
      const code = f.state?.toString().toUpperCase();
      if (!code) continue;
      gallonsByCode.set(code, (gallonsByCode.get(code) || 0) + Number(f.gallons || 0));
    }

    const totalMi = Array.from(milesByCode.values()).reduce((s, v) => s + v, 0);
    const totalGal = Array.from(gallonsByCode.values()).reduce((s, v) => s + v, 0);
    const mpg = totalGal > 0 ? totalMi / totalGal : 0;

    // Build union of codes from rates + miles + fuel (so we don't hide states with no rate)
    const allCodes = new Set<string>();
    rates.forEach(r => allCodes.add(r.jurisdiction_code));
    milesByCode.forEach((_, k) => allCodes.add(k));
    gallonsByCode.forEach((_, k) => allCodes.add(k));

    const missing: string[] = [];
    const out: ReturnLine[] = [];

    for (const code of allCodes) {
      const rate = rateMap.get(code);
      const miles = milesByCode.get(code) || 0;
      const tax_paid_gallons = gallonsByCode.get(code) || 0;

      // For non-IFTA jurisdictions, miles are recorded but not taxed.
      const is_ifta = rate?.is_ifta_jurisdiction ?? true;
      const taxable_gallons = is_ifta && mpg > 0 ? miles / mpg : 0;
      const net_gallons = taxable_gallons - tax_paid_gallons;

      const tax_rate = rate?.tax_rate ?? null;
      const surcharge_rate = rate?.surcharge_rate ?? null;

      let tax_due = 0;
      let surcharge_due = 0;
      if (is_ifta && tax_rate !== null) {
        tax_due = net_gallons * tax_rate;
      } else if (is_ifta && (miles > 0 || tax_paid_gallons > 0)) {
        missing.push(code);
      }
      if (is_ifta && surcharge_rate !== null && SURCHARGE_JURISDICTIONS.has(code)) {
        surcharge_due = taxable_gallons * surcharge_rate;
      }

      out.push({
        code,
        name: rate?.jurisdiction_name || code,
        country: rate?.country || 'US',
        is_ifta,
        miles,
        tax_paid_gallons,
        taxable_gallons,
        net_gallons,
        tax_rate,
        surcharge_rate,
        tax_due,
        surcharge_due,
        total_due: tax_due + surcharge_due,
      });
    }

    out.sort((a, b) => {
      if (a.country !== b.country) return a.country.localeCompare(b.country);
      return a.code.localeCompare(b.code);
    });

    return { lines: out, totalMiles: totalMi, totalGallons: totalGal, missingRates: missing };
  }, [rates, milesRows, fuelRows, ownerFuelRows, isFiltered, selectedTruckIds, selectedUnitSet]);

  const fleetMPG = totalGallons > 0 ? totalMiles / totalGallons : 0;
  const grandTaxDue = lines.reduce((s, l) => s + l.tax_due, 0);
  const grandSurchargeDue = lines.reduce((s, l) => s + l.surcharge_due, 0);
  const grandTotal = grandTaxDue + grandSurchargeDue;

  const toggleTruck = (id: string) => {
    setSelectedTruckIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearTrucks = () => setSelectedTruckIds(new Set());
  const filteredTruckOptions = trucks.filter(t =>
    t.unit_number.toLowerCase().includes(truckSearch.trim().toLowerCase())
  );
  const filterSuffix = isFiltered
    ? `-trucks-${selectedTrucks.map(t => t.unit_number).join('_')}`
    : '';

  function exportCSV() {
    const headers = [
      'Jurisdiction',
      'Country',
      'IFTA',
      'Miles',
      'Taxable Gallons',
      'Tax-Paid Gallons',
      'Net Gallons',
      'Tax Rate',
      'Tax Due',
      'Surcharge Rate',
      'Surcharge Due',
      'Total Due',
    ];
    const rows = lines.map(l => [
      l.code,
      l.country,
      l.is_ifta ? 'Yes' : 'No',
      // Plain numbers: locale thousands separators ("7,843.00") split CSV columns
      l.miles.toFixed(2),
      l.taxable_gallons.toFixed(3),
      l.tax_paid_gallons.toFixed(3),
      l.net_gallons.toFixed(3),
      l.tax_rate == null ? '' : l.tax_rate.toFixed(4),
      l.tax_due.toFixed(2),
      l.surcharge_rate == null ? '' : l.surcharge_rate.toFixed(4),
      l.surcharge_due.toFixed(2),
      l.total_due.toFixed(2),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ifta-return-${quarter}${filterSuffix}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-6 py-4 border-b border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">IFTA Return</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Per-jurisdiction tax due for {quarter} ({start} — {end})
              {isFiltered && (
                <span className="ml-1 font-semibold text-blue-700">
                  · {selectedTrucks.length} truck{selectedTrucks.length === 1 ? '' : 's'} selected
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Truck filter: multi-select, live */}
            <div className="relative" ref={truckPickerRef}>
              <button
                type="button"
                onClick={() => setShowTruckPicker(v => !v)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-semibold ${
                  isFiltered
                    ? 'border-blue-600 bg-blue-50 text-blue-700'
                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
                }`}
                title="Filter the return to selected trucks"
              >
                <Truck className="h-4 w-4" />
                {isFiltered
                  ? selectedTrucks.length <= 3
                    ? selectedTrucks.map(t => `#${t.unit_number}`).join(', ')
                    : `${selectedTrucks.length} trucks`
                  : 'All trucks'}
                <ChevronDown className="h-4 w-4 opacity-60" />
              </button>
              {showTruckPicker && (
                <div className="absolute left-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-xl z-30">
                  <div className="p-2 border-b border-slate-200">
                    <input
                      type="text"
                      autoFocus
                      value={truckSearch}
                      onChange={e => setTruckSearch(e.target.value)}
                      placeholder="Search unit…"
                      className="w-full px-2 py-1.5 text-sm border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto py-1">
                    {filteredTruckOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-slate-500">No trucks match</div>
                    ) : filteredTruckOptions.map(t => {
                      const on = selectedTruckIds.has(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => toggleTruck(t.id)}
                          className={`w-full flex items-center justify-between px-3 py-1.5 text-sm text-left hover:bg-slate-50 ${
                            on ? 'text-blue-700 font-semibold' : 'text-slate-700'
                          }`}
                        >
                          <span>#{t.unit_number}</span>
                          <span className={`inline-flex items-center justify-center w-4 h-4 rounded border ${
                            on ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'
                          }`}>
                            {on && <Check className="h-3 w-3" />}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center justify-between p-2 border-t border-slate-200 text-xs">
                    <button
                      type="button"
                      onClick={clearTrucks}
                      disabled={!isFiltered}
                      className="px-2 py-1 rounded text-slate-600 hover:bg-slate-100 disabled:opacity-40"
                    >
                      All trucks
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowTruckPicker(false)}
                      className="px-2 py-1 rounded bg-blue-600 text-white font-semibold hover:bg-blue-700"
                    >
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={() => setQuarter(shiftQuarter(quarter, -1))}
              className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
              title="Previous quarter"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-semibold text-slate-700 min-w-[90px] text-center">
              {quarter}
            </div>
            <button
              onClick={() => setQuarter(shiftQuarter(quarter, 1))}
              className="p-2 rounded-lg border border-slate-300 hover:bg-slate-50"
              title="Next quarter"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
            <button
              onClick={exportCSV}
              disabled={lines.length === 0}
              className="ml-2 flex items-center gap-2 px-4 py-2 bg-white border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-semibold disabled:opacity-50"
            >
              <Download className="h-4 w-4" />
              CSV
            </button>
          </div>
        </div>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 divide-x divide-slate-200 border-b border-slate-200 bg-slate-50">
        <div className="p-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Miles</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{fmtNum(totalMiles)}</div>
        </div>
        <div className="p-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Tax-Paid Gallons</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{fmtNum(totalGallons, 3)}</div>
        </div>
        <div className="p-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">{isFiltered ? 'Selected MPG' : 'Fleet MPG'}</div>
          <div className="text-2xl font-bold text-slate-900 mt-1 flex items-center gap-2">
            {fleetMPG > 0 ? fmtNum(fleetMPG, 2) : '—'}
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </div>
        </div>
        <div className="p-4">
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Tax Due</div>
          <div className={`text-2xl font-bold mt-1 ${grandTotal >= 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
            {fmtUSD(grandTotal)}
          </div>
        </div>
      </div>

      {isFiltered && (
        <div className="bg-blue-50 border-b border-blue-200 px-6 py-3 flex flex-wrap items-center gap-2 text-sm text-blue-900">
          <span className="font-semibold">Filtered view:</span>
          <span>only the selected trucks. MPG and tax due are for these units, not the fleet. The filed return uses all trucks.</span>
          <span className="flex flex-wrap items-center gap-1 ml-1">
            {selectedTrucks.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleTruck(t.id)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-blue-300 text-blue-800 text-xs font-semibold hover:bg-blue-100"
                title="Remove from filter"
              >
                #{t.unit_number}
                <X className="h-3 w-3" />
              </button>
            ))}
            <button
              type="button"
              onClick={clearTrucks}
              className="ml-1 text-xs font-semibold text-blue-700 underline hover:text-blue-900"
            >
              Show all trucks
            </button>
          </span>
        </div>
      )}

      {!loading && totalMiles === 0 && totalGallons > 0 && (
        <div className="bg-rose-50 border-b border-rose-200 px-6 py-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-rose-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-rose-900">
            <strong>No miles imported for {quarter}{isFiltered ? ' on the selected trucks' : ''}.</strong>{' '}
            Miles don't come from the ELD automatically: export the ProLogs Vehicle mileage report for these dates and
            import it on the Mileage tab. Until then, tax due is not correct.
            {onGoToMileage && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={onGoToMileage}
                  className="font-semibold text-rose-700 underline hover:text-rose-900"
                >
                  Go to Mileage
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {missingRates.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-start gap-2">
          <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Missing rates for {quarter}:</strong> {missingRates.join(', ')}.{' '}
            Enter rates in the <em>Tax Rates</em> tab before filing.
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading return...
        </div>
      ) : lines.length === 0 ? (
        <div className="py-16 text-center text-slate-500">
          <FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          No miles or fuel recorded for {quarter}{isFiltered ? ' on the selected trucks' : ''}.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase">Jurisdiction</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Miles</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Taxable Gal</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Tax-Paid Gal</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Net Gal</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Rate</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Tax Due</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Surcharge</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-slate-700 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lines.map(l => (
                <tr key={l.code} className={l.is_ifta ? '' : 'bg-slate-50/50 text-slate-500'}>
                  <td className="px-4 py-2 font-medium">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-8 h-5 rounded text-[10px] font-bold bg-slate-100 text-slate-600">
                        {l.code}
                      </span>
                      <span>{l.name}</span>
                      {!l.is_ifta && (
                        <span className="text-[10px] font-semibold text-slate-400 uppercase ml-1">Non-IFTA</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtNum(l.miles)}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{l.is_ifta ? fmtNum(l.taxable_gallons, 3) : '—'}</td>
                  <td className="px-4 py-2 text-right tabular-nums">{fmtNum(l.tax_paid_gallons, 3)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${l.net_gallons > 0 ? 'text-rose-600' : l.net_gallons < 0 ? 'text-emerald-600' : ''}`}>
                    {l.is_ifta ? fmtNum(l.net_gallons, 3) : '—'}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{l.tax_rate == null ? '—' : l.tax_rate.toFixed(4)}</td>
                  <td className={`px-4 py-2 text-right tabular-nums ${l.tax_due > 0 ? 'text-rose-600' : l.tax_due < 0 ? 'text-emerald-600' : ''}`}>
                    {l.tax_rate == null && l.is_ifta && l.miles > 0 ? '—' : fmtUSD(l.tax_due)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {SURCHARGE_JURISDICTIONS.has(l.code) && l.surcharge_rate !== null
                      ? fmtUSD(l.surcharge_due)
                      : SURCHARGE_JURISDICTIONS.has(l.code) ? '—' : ''}
                  </td>
                  <td className={`px-4 py-2 text-right tabular-nums font-semibold ${l.total_due > 0 ? 'text-rose-600' : l.total_due < 0 ? 'text-emerald-600' : ''}`}>
                    {l.is_ifta ? fmtUSD(l.total_due) : ''}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
              <tr>
                <td className="px-4 py-3 font-bold uppercase text-xs tracking-wider">Grand Total</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">{fmtNum(totalMiles)}</td>
                <td colSpan={2} className="px-4 py-3 text-right tabular-nums font-bold">{fmtNum(totalGallons, 3)} gal</td>
                <td className="px-4 py-3 text-right text-xs opacity-75">
                  MPG: {fleetMPG > 0 ? fmtNum(fleetMPG, 2) : '—'}
                </td>
                <td colSpan={1}></td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">{fmtUSD(grandTaxDue)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">{fmtUSD(grandSurchargeDue)}</td>
                <td className="px-4 py-3 text-right tabular-nums font-bold">{fmtUSD(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
